import { differenceInMilliseconds, isDate } from 'date-fns';
import { FormSectionState, StateField, StateFieldType } from '@navstation-portal/reporting/data-model/vessel-report';
import { isString } from '@ngx-translate/core';
import { getFieldFromState } from '../../offshore-vm-builder-helpers';

export interface OffshoreOperationalFormVmSections {
  [key: string]: (() => FormSectionState | undefined) | undefined;
  reportTime?: () => FormSectionState | undefined;
  reportDetails?: () => FormSectionState | undefined;
  operatingConditions?: () => FormSectionState | undefined;
}

export type OffshoreOperationalVm = ReturnType<typeof buildOffshoreOperationalVm>;

export function buildOffshoreOperationalVm(store: OffshoreOperationalFormVmSections) {
  const getReportDate = (section: 'reportDetails', field: string) => {
    const sectionState = store[section]?.() ?? {};
    const reportField = getFieldFromState({ [section]: sectionState }, section, field) as StateField<StateFieldType>;

    if (reportField?.value == null || (!isString(reportField.value) && !isDate(reportField.value))) return;

    const toDate = isDate(reportField.value) ? (reportField.value as Date) : new Date(reportField.value as string);
    return isNaN(toDate.getTime()) ? undefined : toDate;
  };

  const reportPeriod = (section: 'reportDetails') => {
    const fromDate = getReportDate(section, 'reportFrom');
    if (!fromDate) return;
    const toDate = getReportDate(section, 'reportTo');
    if (!toDate) return;
    const diffInMs = differenceInMilliseconds(toDate, fromDate);
    const diffInHours = diffInMs / (1000 * 60 * 60);
    return Math.round(diffInHours * 100) / 100;
  };

  const getOpCondition = (field: string) => {
    const opConditionField = getFieldFromState(
      { operatingConditions: store.operatingConditions?.() ?? {} },
      'operatingConditions',
      field
    ) as StateField<StateFieldType>;

    if (opConditionField?.value == null) return;

    const value = Number(opConditionField.value);
    return isNaN(value) ? undefined : value;
  };

  const trim = () => {
    const aftValue = getOpCondition('draughtAft');
    const forwardValue = getOpCondition('draughtForward');
    if (aftValue == null || forwardValue == null) return undefined;
    return aftValue - forwardValue;
  };

  return {
    reportPeriod,
    trim,
  };
}
